import React, { useState, useRef, useEffect } from 'react';
import {
  FunnelIcon,
  ChevronDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import DateTimePickerInput from './DateTimePickerInput';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterSection {
  id: string;
  /** Shown in the main filter-type list (e.g. Filters ▾). */
  label: string;
  /** Optional title for the value panel; defaults to `label`. Use for extra detail (e.g. counts) without cluttering the list. */
  valuePanelLabel?: string;
  type: 'multi-select' | 'single-select' | 'text' | 'searchable-select' | 'searchable-multi-select' | 'time-range' | 'duration-range';
  options?: FilterOption[];
  placeholder?: string;
  helpText?: string;
  disabled?: boolean;
  disabledMessage?: string;
  /**
   * For searchable-select: empty string still counts as an active choice (e.g. "All Users") so the filter row shows a badge.
   * When `assignedToMe` is `'true'`, badge is suppressed for this section if `suppressEmptySelectWhenAssignedToMe` is set.
   */
  treatEmptyStringAsSelected?: boolean;
  /** When treatEmptyStringAsSelected is true, do not show a badge for empty value if Assigned To Me is Yes. */
  suppressEmptySelectWhenAssignedToMe?: boolean;
  /**
   * For multi / searchable-multi-select: synthetic option value meaning "all" (e.g. all task types).
   * Select All sets only this value; toggling it replaces any specific selections.
   */
  allOptionValue?: string;
  /** For `time-range` custom From/To: minute dropdown step (default 1 = every minute). */
  timeRangeMinuteStep?: number;
  /**
   * For `time-range` custom From/To: when enabled, any selection coerces the time component to a fixed day-bound time.
   * Also initializes empty custom range values to today's date with these times.
   *
   * Intended for KPI/Bandwidth "Duration" custom range:
   * - From defaults to 00:00
   * - To defaults to 23:59
   */
  timeRangeCoerceToDayBounds?: boolean;
  /** Used when `timeRangeCoerceToDayBounds` is true. Default "00:00". */
  timeRangeDefaultFromTime?: string;
  /** Used when `timeRangeCoerceToDayBounds` is true. Default "23:59". */
  timeRangeDefaultToTime?: string;
}

interface FilterValues {
  [key: string]: string | string[];
}

interface FilterDropdownProps {
  sections: FilterSection[];
  values: FilterValues;
  onChange: (sectionId: string, value: string | string[]) => void;
  onApply?: (sectionId?: string, value?: string | string[]) => void;
  className?: string;
  /** Add to the badge count for filters managed outside this menu (e.g. KPI assignment group quick pick) */
  extraActiveFilterCount?: number;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  sections,
  values,
  onChange,
  onApply,
  className = '',
  extraActiveFilterCount = 0,
}) => {
  const [isFilterTypeOpen, setIsFilterTypeOpen] = useState(false);
  const [selectedFilterType, setSelectedFilterType] = useState<string | null>(null);
  const [isValueDropdownOpen, setIsValueDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [textInputValue, setTextInputValue] = useState('');

  // Pending values - only applied when user clicks "Done"
  const [pendingMultiSelect, setPendingMultiSelect] = useState<string[]>([]);
  const [pendingSingleSelect, setPendingSingleSelect] = useState<string>('');

  // Custom date range state
  const [pendingDateFrom, setPendingDateFrom] = useState<string>('');
  const [pendingDateTo, setPendingDateTo] = useState<string>('');
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [dateRangeError, setDateRangeError] = useState<string>('');

  // Duration range state (for time entered filter)
  const [pendingDurationMin, setPendingDurationMin] = useState<string>('');
  const [pendingDurationMax, setPendingDurationMax] = useState<string>('');
  const [isCustomDuration, setIsCustomDuration] = useState(false);

  const filterTypeRef = useRef<HTMLDivElement>(null);
  const valueDropdownRef = useRef<HTMLDivElement>(null);

  const sectionCountsAsActive = (section: FilterSection): boolean => {
    const value = values[section.id];
    if (section.type === 'multi-select' || section.type === 'searchable-multi-select') {
      const arr = (value as string[]) || [];
      if ((section.options?.length ?? 0) === 0) return false;
      return arr.length > 0;
    }
    if (section.type === 'single-select' || section.type === 'searchable-select' || section.type === 'time-range') {
      const s = typeof value === 'string' ? value : '';
      if (s !== '') return true;
      if (section.treatEmptyStringAsSelected) {
        if (section.suppressEmptySelectWhenAssignedToMe && values.assignedToMe === 'true') return false;
        return true;
      }
      return false;
    }
    if (section.type === 'text') {
      return Boolean(value);
    }
    if (section.type === 'duration-range') {
      const s = typeof value === 'string' ? value : '';
      return Boolean(s);
    }
    return false;
  };

  // Count active filters
  const activeFilterCount =
    extraActiveFilterCount + sections.reduce((count, section) => count + (sectionCountsAsActive(section) ? 1 : 0), 0);

  // Get selected section
  const selectedSection = sections.find(s => s.id === selectedFilterType);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideFilterType = filterTypeRef.current && !filterTypeRef.current.contains(target);
      const isOutsideValueDropdown = valueDropdownRef.current && !valueDropdownRef.current.contains(target);

      // Check if click is inside a calendar dropdown (rendered via portal)
      // Calendar dropdowns have data-calendar-portal attribute
      let isInsideCalendar = false;
      if (target instanceof Element) {
        const calendarElement = target.closest('[data-calendar-portal]');
        isInsideCalendar = calendarElement !== null;
      }

      if (isOutsideFilterType && isOutsideValueDropdown && !isInsideCalendar) {
        setIsFilterTypeOpen(false);
        setIsValueDropdownOpen(false);
        setSelectedFilterType(null);
        setSearchTerm('');
        setTextInputValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Validate date range
  const validateDateRange = (from: string, to: string): string => {
    if (!from || !to) return '';
    const fromDate = new Date(from);
    const toDate = new Date(to);
    if (toDate <= fromDate) {
      return 'To date/time must be greater than From date/time';
    }
    return '';
  };

  // Handle filter type selection
  const handleFilterTypeSelect = (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (section?.disabled) return;

    setSelectedFilterType(sectionId);
    setIsFilterTypeOpen(false);
    setIsValueDropdownOpen(true);
    setSearchTerm('');
    setIsCustomDateRange(false);
    setDateRangeError('');

    // Initialize pending values from current values
    if (section?.type === 'multi-select' || section?.type === 'searchable-multi-select') {
      const current = (values[sectionId] as string[]) || [];
      const opts = section?.options ?? [];
      setPendingMultiSelect(opts.length === 0 ? [] : current);
    } else if (section?.type === 'single-select' || section?.type === 'searchable-select') {
      setPendingSingleSelect((values[sectionId] as string) || '');
    } else if (section?.type === 'text') {
      setTextInputValue((values[sectionId] as string) || '');
    } else if (section?.type === 'time-range') {
      const currentValue = values[sectionId] as string || '';
      // Check if it's a custom range (contains 'custom:')
      if (currentValue.startsWith('custom:')) {
        const [, dates] = currentValue.split('custom:');
        const [from, to] = dates.split(',');
        setPendingDateFrom(from || '');
        setPendingDateTo(to || '');
        setIsCustomDateRange(true);
        setPendingSingleSelect('custom');
        // Validate existing dates
        if (from && to) {
          const error = validateDateRange(from, to);
          setDateRangeError(error);
        }
      } else {
        setPendingSingleSelect(currentValue);
        setPendingDateFrom('');
        setPendingDateTo('');
      }
    } else if (section?.type === 'duration-range') {
      const currentValue = values[sectionId] as string || '';
      // Check if it's a custom range (contains 'custom:')
      if (currentValue.startsWith('custom:')) {
        const [, range] = currentValue.split('custom:');
        const [minVal, maxVal] = range.split(',');
        setPendingDurationMin(minVal || '');
        setPendingDurationMax(maxVal || '');
        setIsCustomDuration(true);
        setPendingSingleSelect('custom');
      } else {
        setPendingSingleSelect(currentValue);
        setPendingDurationMin('');
        setPendingDurationMax('');
        setIsCustomDuration(false);
      }
    }
  };

  // Handle multi-select toggle (updates pending state only)
  const handleMultiSelectToggle = (optionValue: string) => {
    const allV = selectedSection?.allOptionValue;
    if (allV && optionValue === allV) {
      setPendingMultiSelect((p) => (p.includes(allV) ? [] : [allV]));
      return;
    }
    setPendingMultiSelect((p) => {
      const base = allV && p.includes(allV) ? p.filter((v) => v !== allV) : [...p];
      if (base.includes(optionValue)) return base.filter((v) => v !== optionValue);
      return [...base, optionValue];
    });
  };

  // Handle single select toggle (updates pending state only)
  const handleSingleSelectToggle = (optionValue: string) => {
    setPendingSingleSelect(optionValue);
  };

  // Handle apply for multi-select (called when Done is clicked)
  const handleApplyMultiSelect = () => {
    if (!selectedFilterType) return;
    onChange(selectedFilterType, pendingMultiSelect);
    setIsValueDropdownOpen(false);
    setSelectedFilterType(null);
    setPendingMultiSelect([]);
  };

  // Handle apply for single select (called when Done is clicked)
  const handleApplySingleSelect = () => {
    if (!selectedFilterType) return;
    onChange(selectedFilterType, pendingSingleSelect);
    setIsValueDropdownOpen(false);
    setSelectedFilterType(null);
    setPendingSingleSelect('');
  };

  // Handle cancel - close without applying
  const handleCancel = () => {
    setIsValueDropdownOpen(false);
    setSelectedFilterType(null);
    setPendingMultiSelect([]);
    setPendingSingleSelect('');
    setTextInputValue('');
    setSearchTerm('');
    setPendingDateFrom('');
    setPendingDateTo('');
    setIsCustomDateRange(false);
    setDateRangeError('');
    setPendingDurationMin('');
    setPendingDurationMax('');
    setIsCustomDuration(false);
  };

  // Handle date from change
  const handleDateFromChange = (value: string) => {
    const coerce = Boolean(selectedSection?.timeRangeCoerceToDayBounds);
    const fromTime = selectedSection?.timeRangeDefaultFromTime || '00:00';
    const normalized = coerce ? normalizeDateTimeLocalToTime(value, fromTime) : value;
    setPendingDateFrom(normalized);
    if (normalized && pendingDateTo) {
      const error = validateDateRange(normalized, pendingDateTo);
      setDateRangeError(error);
    } else {
      setDateRangeError('');
    }
  };

  // Handle date to change
  const handleDateToChange = (value: string) => {
    const coerce = Boolean(selectedSection?.timeRangeCoerceToDayBounds);
    const toTime = selectedSection?.timeRangeDefaultToTime || '23:59';
    const normalized = coerce ? normalizeDateTimeLocalToTime(value, toTime) : value;
    setPendingDateTo(normalized);
    if (pendingDateFrom && normalized) {
      const error = validateDateRange(pendingDateFrom, normalized);
      setDateRangeError(error);
    } else {
      setDateRangeError('');
    }
  };

  // Handle apply for time-range with custom dates
  const handleApplyTimeRange = () => {
    if (!selectedFilterType) return;
    if (isCustomDateRange && pendingDateFrom && pendingDateTo) {
      // Validate before applying
      const error = validateDateRange(pendingDateFrom, pendingDateTo);
      if (error) {
        setDateRangeError(error);
        return;
      }
      onChange(selectedFilterType, `custom:${pendingDateFrom},${pendingDateTo}`);
    } else if (pendingSingleSelect && pendingSingleSelect !== 'custom') {
      onChange(selectedFilterType, pendingSingleSelect);
    } else {
      // Clear the filter if nothing is selected
      onChange(selectedFilterType, '');
    }
    setIsValueDropdownOpen(false);
    setSelectedFilterType(null);
    setPendingSingleSelect('');
    setPendingDateFrom('');
    setPendingDateTo('');
    setIsCustomDateRange(false);
    setDateRangeError('');
  };

  // Handle time range option selection
  const handleTimeRangeSelect = (value: string) => {
    if (value === 'custom') {
      setIsCustomDateRange(true);
      setPendingSingleSelect('custom');
      const coerce = Boolean(selectedSection?.timeRangeCoerceToDayBounds);
      if (coerce) {
        const fromTime = selectedSection?.timeRangeDefaultFromTime || '00:00';
        const toTime = selectedSection?.timeRangeDefaultToTime || '23:59';
        const today = getTodayDateOnlyLocal();
        setPendingDateFrom((cur) => cur || `${today}T${fromTime}`);
        setPendingDateTo((cur) => cur || `${today}T${toTime}`);
      }
    } else {
      setIsCustomDateRange(false);
      setPendingSingleSelect(value);
    }
  };

  const getTodayDateOnlyLocal = (): string => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const normalizeDateTimeLocalToTime = (raw: string, timeHHmm: string): string => {
    if (!raw) return '';
    const datePart = raw.split('T')[0];
    if (!datePart) return '';
    return `${datePart}T${timeHHmm}`;
  };

  // Handle duration range option selection
  const handleDurationRangeSelect = (value: string) => {
    if (value === 'custom') {
      setIsCustomDuration(true);
      setPendingSingleSelect('custom');
    } else {
      setIsCustomDuration(false);
      setPendingSingleSelect(value);
    }
  };

  // Handle apply for duration-range
  const handleApplyDurationRange = () => {
    if (!selectedFilterType) return;
    if (isCustomDuration && (pendingDurationMin || pendingDurationMax)) {
      onChange(selectedFilterType, `custom:${pendingDurationMin || ''},${pendingDurationMax || ''}`);
    } else if (pendingSingleSelect && pendingSingleSelect !== 'custom') {
      onChange(selectedFilterType, pendingSingleSelect);
    } else {
      // Clear the filter if nothing is selected
      onChange(selectedFilterType, '');
    }
    setIsValueDropdownOpen(false);
    setSelectedFilterType(null);
    setPendingSingleSelect('');
    setPendingDurationMin('');
    setPendingDurationMax('');
    setIsCustomDuration(false);
  };

  // Handle select all for multi-select (updates pending state)
  const handleSelectAll = () => {
    if (!selectedSection?.options?.length) return;
    const allV = selectedSection.allOptionValue;
    if (allV) {
      if (pendingMultiSelect.length === 1 && pendingMultiSelect[0] === allV) {
        setPendingMultiSelect([]);
      } else {
        setPendingMultiSelect([allV]);
      }
      return;
    }
    if (pendingMultiSelect.length === selectedSection.options.length) {
      setPendingMultiSelect([]);
    } else {
      setPendingMultiSelect(selectedSection.options.map((opt) => opt.value));
    }
  };

  // Handle clear for current filter (updates pending state)
  const handleClearCurrent = () => {
    if (selectedSection?.type === 'multi-select' || selectedSection?.type === 'searchable-multi-select') {
      setPendingMultiSelect([]);
    } else {
      setPendingSingleSelect('');
    }
  };

  // Handle text input apply
  const handleTextApply = () => {
    if (!selectedFilterType) return;
    const valueToApply = textInputValue.trim();
    onChange(selectedFilterType, valueToApply);
    if (onApply) {
      console.log('FilterDropdown: Calling onApply with', selectedFilterType, valueToApply);
      onApply(selectedFilterType, valueToApply);
    }
    setIsValueDropdownOpen(false);
    setSelectedFilterType(null);
    setTextInputValue('');
  };

  // Get filtered options for searchable select
  const getFilteredOptions = () => {
    if (!selectedSection?.options) return [];
    if (!searchTerm) return selectedSection.options;
    return selectedSection.options.filter(opt =>
      opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Get display text for a section's current value
  const getSectionValueBadge = (section: FilterSection): string | null => {
    const value = values[section.id];
    if (section.type === 'multi-select' || section.type === 'searchable-multi-select') {
      const selectedValues = (value as string[]) || [];
      if (selectedValues.length === 0) return null;
      if ((section.options?.length ?? 0) === 0) return null;
      return selectedValues.length.toString();
    }
    if (section.type === 'single-select' || section.type === 'searchable-select' || section.type === 'time-range') {
      const s = typeof value === 'string' ? value : '';
      if (s !== '') return '1';
      if (section.treatEmptyStringAsSelected) {
        if (section.suppressEmptySelectWhenAssignedToMe && values.assignedToMe === 'true') return null;
        return '1';
      }
      return null;
    }
    if (section.type === 'text') {
      if (!value) return null;
      return '1';
    }
    if (section.type === 'duration-range') {
      const s = typeof value === 'string' ? value : '';
      return s ? '1' : null;
    }
    return null;
  };

  // Render value dropdown content based on filter type
  const renderValueDropdown = () => {
    if (!selectedSection) return null;

    if (selectedSection.disabled) {
      return (
        <div className="p-4 text-sm text-gray-500 italic">
          {selectedSection.disabledMessage || 'This filter is not available'}
        </div>
      );
    }

    const valuePanelHeaderLabel = selectedSection.valuePanelLabel ?? selectedSection.label;

    switch (selectedSection.type) {
      case 'multi-select': {
        const options = selectedSection.options || [];
        const allV = selectedSection.allOptionValue;
        const isAllSelected = allV
          ? pendingMultiSelect.length === 1 && pendingMultiSelect[0] === allV
          : options.length > 0 && pendingMultiSelect.length === options.length;

        return (
          <div className="p-2">
            {/* Header with Select All / Clear */}
            <div className="flex items-center justify-between px-2 py-1 mb-2 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase">{valuePanelHeaderLabel}</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-xs text-indigo-600 hover:text-indigo-800"
                >
                  {isAllSelected ? 'Deselect All' : 'Select All'}
                </button>
                {pendingMultiSelect.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearCurrent}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            {/* Options */}
            <div className="max-h-60 overflow-y-auto">
              {options.map(option => {
                const isSelected = pendingMultiSelect.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 ${
                      isSelected ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <div
                      className={`w-4 h-4 flex items-center justify-center border rounded ${
                        isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 bg-white'
                      }`}
                    >
                      {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                    </div>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleMultiSelectToggle(option.value)}
                      className="sr-only"
                    />
                    <span className={`text-sm ${isSelected ? 'text-indigo-700 font-medium' : 'text-gray-700'}`}>
                      {option.label}
                    </span>
                  </label>
                );
              })}
            </div>
            {/* Cancel and Done buttons */}
            <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyMultiSelect}
                className="px-3 py-1 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </div>
        );
      }

      case 'single-select': {
        const options = selectedSection.options || [];

        return (
          <div className="p-2">
            <div className="flex items-center justify-between px-2 py-1 mb-2 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase">{valuePanelHeaderLabel}</span>
              {pendingSingleSelect && (
                <button
                  type="button"
                  onClick={handleClearCurrent}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="max-h-60 overflow-y-auto">
              {options.map(option => {
                const isSelected = pendingSingleSelect === option.value;
                return (
                  <label
                    key={option.value}
                    className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 ${
                      isSelected ? 'bg-indigo-50' : ''
                    }`}
                    onClick={() => handleSingleSelectToggle(option.value)}
                  >
                    <span className={`text-sm ${isSelected ? 'text-indigo-700 font-medium' : 'text-gray-700'}`}>
                      {option.label}
                    </span>
                    {isSelected && <CheckIcon className="h-4 w-4 text-indigo-600" />}
                  </label>
                );
              })}
            </div>
            {/* Cancel and Done buttons */}
            <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplySingleSelect}
                className="px-3 py-1 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </div>
        );
      }

      case 'searchable-select': {
        const filteredOptions = getFilteredOptions();

        return (
          <div className="p-2">
            <div className="flex items-center justify-between px-2 py-1 mb-2 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase">{valuePanelHeaderLabel}</span>
              {pendingSingleSelect && (
                <button
                  type="button"
                  onClick={handleClearCurrent}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
            {/* Search input */}
            <div className="relative mb-2">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                autoFocus
              />
            </div>
            {/* Options */}
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.map(option => {
                const isSelected = pendingSingleSelect === option.value;
                return (
                  <label
                    key={option.value}
                    className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 ${
                      isSelected ? 'bg-indigo-50' : ''
                    }`}
                    onClick={() => handleSingleSelectToggle(option.value)}
                  >
                    <span className={`text-sm ${isSelected ? 'text-indigo-700 font-medium' : 'text-gray-700'}`}>
                      {option.label}
                    </span>
                    {isSelected && <CheckIcon className="h-4 w-4 text-indigo-600" />}
                  </label>
                );
              })}
              {filteredOptions.length === 0 && (
                <div className="px-2 py-4 text-sm text-gray-500 text-center">
                  No options found
                </div>
              )}
            </div>
            {/* Cancel and Done buttons */}
            <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplySingleSelect}
                className="px-3 py-1 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </div>
        );
      }

      case 'text': {
        return (
          <div className="p-3">
            <div className="flex items-center justify-between px-1 py-1 mb-2 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase">{valuePanelHeaderLabel}</span>
            </div>
            <input
              type="text"
              value={textInputValue}
              onChange={(e) => setTextInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleTextApply();
                }
              }}
              placeholder={selectedSection.placeholder || 'Enter value...'}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
              autoFocus
            />
            {selectedSection.helpText && (
              <p className="mt-1 text-xs text-gray-500">{selectedSection.helpText}</p>
            )}
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleTextApply}
                className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </div>
        );
      }

      case 'time-range': {
        const options = selectedSection.options || [];
        const allOptions = [...options, { value: 'custom', label: 'Custom Range' }];

        return (
          <div className="p-2">
            <div className="flex items-center justify-between px-2 py-1 mb-2 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase">{valuePanelHeaderLabel}</span>
              {(pendingSingleSelect || isCustomDateRange) && (
                <button
                  type="button"
                  onClick={() => {
                    setPendingSingleSelect('');
                    setPendingDateFrom('');
                    setPendingDateTo('');
                    setIsCustomDateRange(false);
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="max-h-60 overflow-y-auto">
              {allOptions.map(option => {
                const isSelected = pendingSingleSelect === option.value;
                return (
                  <label
                    key={option.value}
                    className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 ${
                      isSelected ? 'bg-indigo-50' : ''
                    }`}
                    onClick={() => handleTimeRangeSelect(option.value)}
                  >
                    <span className={`text-sm ${isSelected ? 'text-indigo-700 font-medium' : 'text-gray-700'}`}>
                      {option.label}
                    </span>
                    {isSelected && <CheckIcon className="h-4 w-4 text-indigo-600" />}
                  </label>
                );
              })}
            </div>
            {/* Custom date range inputs */}
            {isCustomDateRange && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                  <DateTimePickerInput
                    value={pendingDateFrom}
                    onChange={(value) => handleDateFromChange(value)}
                    max={pendingDateTo || undefined}
                    error={!!dateRangeError}
                    minuteStep={selectedSection.timeRangeMinuteStep ?? 1}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                  <DateTimePickerInput
                    value={pendingDateTo}
                    onChange={(value) => handleDateToChange(value)}
                    min={pendingDateFrom || undefined}
                    error={!!dateRangeError}
                    minuteStep={selectedSection.timeRangeMinuteStep ?? 1}
                  />
                  {dateRangeError && (
                    <p className="mt-1 text-xs text-red-600">{dateRangeError}</p>
                  )}
                </div>
              </div>
            )}
            {/* Cancel and Done buttons */}
            <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyTimeRange}
                disabled={isCustomDateRange && (!pendingDateFrom || !pendingDateTo || !!dateRangeError)}
                className="px-3 py-1 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Done
              </button>
            </div>
          </div>
        );
      }

      case 'searchable-multi-select': {
        const filteredOptions = getFilteredOptions();
        const allOptions = selectedSection.options || [];
        const allV = selectedSection.allOptionValue;
        const isAllSelected = allV
          ? pendingMultiSelect.length === 1 && pendingMultiSelect[0] === allV
          : allOptions.length > 0 && pendingMultiSelect.length === allOptions.length;

        return (
          <div className="p-2">
            <div className="flex items-center justify-between px-2 py-1 mb-2 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase">{valuePanelHeaderLabel}</span>
              <div className="flex gap-2">
                {allOptions.length > 0 && (
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-xs text-indigo-600 hover:text-indigo-800"
                  >
                    {isAllSelected ? 'Deselect All' : 'Select All'}
                  </button>
                )}
                {pendingMultiSelect.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPendingMultiSelect([])}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            {/* Search input */}
            <div className="relative mb-2">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                autoFocus
              />
            </div>
            {/* Selected count */}
            {pendingMultiSelect.length > 0 && (
              <div className="mb-2 px-2 text-xs text-indigo-600 font-medium">
                {pendingMultiSelect.length} selected
              </div>
            )}
            {/* Options */}
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.map(option => {
                const isSelected = pendingMultiSelect.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 ${
                      isSelected ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <div
                      className={`w-4 h-4 flex items-center justify-center border rounded ${
                        isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 bg-white'
                      }`}
                    >
                      {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                    </div>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleMultiSelectToggle(option.value)}
                      className="sr-only"
                    />
                    <span className={`text-sm ${isSelected ? 'text-indigo-700 font-medium' : 'text-gray-700'}`}>
                      {option.label}
                    </span>
                  </label>
                );
              })}
              {filteredOptions.length === 0 && (
                <div className="px-2 py-4 text-sm text-gray-500 text-center">
                  No options found
                </div>
              )}
            </div>
            {/* Cancel and Done buttons */}
            <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyMultiSelect}
                className="px-3 py-1 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </div>
        );
      }

      case 'duration-range': {
        const options = selectedSection.options || [];

        return (
          <div className="p-2">
            <div className="flex items-center justify-between px-2 py-1 mb-2 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase">{valuePanelHeaderLabel}</span>
              {(pendingSingleSelect || isCustomDuration) && (
                <button
                  type="button"
                  onClick={() => {
                    setPendingSingleSelect('');
                    setPendingDurationMin('');
                    setPendingDurationMax('');
                    setIsCustomDuration(false);
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="max-h-60 overflow-y-auto">
              {options.map(option => {
                const isSelected = pendingSingleSelect === option.value;
                return (
                  <label
                    key={option.value}
                    className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 ${
                      isSelected ? 'bg-indigo-50' : ''
                    }`}
                    onClick={() => handleDurationRangeSelect(option.value)}
                  >
                    <span className={`text-sm ${isSelected ? 'text-indigo-700 font-medium' : 'text-gray-700'}`}>
                      {option.label}
                    </span>
                    {isSelected && <CheckIcon className="h-4 w-4 text-indigo-600" />}
                  </label>
                );
              })}
            </div>
            {/* Custom duration range inputs */}
            {isCustomDuration && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Min (minutes)</label>
                  <input
                    type="number"
                    min="0"
                    value={pendingDurationMin}
                    onChange={(e) => setPendingDurationMin(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="e.g., 30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Max (minutes)</label>
                  <input
                    type="number"
                    min="0"
                    value={pendingDurationMax}
                    onChange={(e) => setPendingDurationMax(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="e.g., 120"
                  />
                </div>
                <p className="text-xs text-gray-500">Leave either field empty for open-ended range</p>
              </div>
            )}
            {/* Cancel and Done buttons */}
            <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyDurationRange}
                className="px-3 py-1 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Filter Type Dropdown */}
      <div ref={filterTypeRef} className="relative">
        <button
          type="button"
          onClick={() => {
            setIsFilterTypeOpen(!isFilterTypeOpen);
            if (isValueDropdownOpen) {
              setIsValueDropdownOpen(false);
              setSelectedFilterType(null);
            }
          }}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 transition-colors ${
            activeFilterCount > 0
              ? 'border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <FunnelIcon className="h-5 w-5" />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-indigo-600 text-white rounded-full">
              {activeFilterCount}
            </span>
          )}
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${isFilterTypeOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Filter Type Options */}
        {isFilterTypeOpen && (
          <div className="absolute z-[100] mt-1 w-56 rounded-lg border border-gray-200 bg-white shadow-lg">
            <div className="py-1">
              {sections.map(section => {
                const badge = getSectionValueBadge(section);
                const hasValue = badge !== null;

                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => handleFilterTypeSelect(section.id)}
                    disabled={section.disabled}
                    className={`w-full flex items-center justify-between px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                      section.disabled ? 'opacity-50 cursor-not-allowed' : ''
                    } ${hasValue ? 'text-indigo-700 font-medium' : 'text-gray-700'}`}
                  >
                    <span>{section.label}</span>
                    {hasValue && (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-indigo-100 text-indigo-700 rounded-full">
                        {badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Value Dropdown (appears adjacent when filter type is selected) */}
      {isValueDropdownOpen && selectedSection && (
        <div ref={valueDropdownRef} className="relative">
          <div className="absolute left-0 -top-5 z-[100] w-64 rounded-lg border border-gray-200 bg-white shadow-lg">
            {renderValueDropdown()}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
